"use client";

import { ImageGallery, ImageGalleryCategory, ImageGalleryImage, useImageGallery } from "@/components/common/gallery";
import { GalleryCategories, GalleryImageCounter, GalleryNavigationButtons } from "@/components/common/gallery/overlays";
import { prepareGalleryImages } from "@/utils/sup/gallery";
import * as React from "react";
import { type GalleryProps, getSortedCategories } from "./shared";

// Re-export GalleryProps for external consumers
export type { GalleryProps };

// Internal component that uses gallery context to sync category
function GalleryContent({
	images,
	sortedCategories,
	selectedCategory,
	setSelectedCategory,
}: {
	images: ImageGalleryImage[];
	sortedCategories: ImageGalleryCategory[];
	selectedCategory: ImageGalleryCategory | undefined;
	setSelectedCategory: (category: ImageGalleryCategory | undefined) => void;
}) {
	const { currentIndex, isFullscreen, goToPrevious, goToNext } = useImageGallery();

	// Sync selected category with current image
	React.useEffect(() => {
		const currentImage = images[currentIndex];
		if (currentImage?.space) {
			const currentCategory = sortedCategories.find(cat => cat.code === currentImage.space);
			if (currentCategory && currentCategory.code !== selectedCategory?.code) {
				setSelectedCategory(currentCategory);
			}
		}
	}, [currentIndex, images, sortedCategories, selectedCategory, setSelectedCategory]);

	const showCategories = isFullscreen && sortedCategories.length > 0;
	const canGoPrevious = currentIndex > 0;
	const canGoNext = currentIndex < images.length - 1;

	return (
		<>
			{/* Custom Image Counter - hidden when category nav is visible */}
			{!showCategories && (
				<GalleryImageCounter
					className="opacity-100 md:opacity-0 md:group-hover/card:opacity-100 transition-opacity duration-200"
					showImageName={true}
				/>
			)}

			{/* Navigation Buttons */}
			<GalleryNavigationButtons
				onPrevious={goToPrevious}
				onNext={goToNext}
				canGoPrevious={canGoPrevious}
				canGoNext={canGoNext}
			/>

			{/* Category Navigation - Only show in fullscreen mode */}
			{showCategories && (
				<GalleryCategories
					categories={sortedCategories}
					selectedCategory={selectedCategory}
					onCategoryChange={setSelectedCategory}
				/>
			)}
		</>
	);
}

export const GallerySmall = React.memo(function Gallery({
	coverImage,
	mediaList = [],
	type,
	projectId,
	className,
	isLoading = false,
	shouldPrioritizeCoverImage = true,
	sizes = "(max-width: 480px) 40vw, (max-width: 768px) 30vw, 320px",
	onMainImageClick,
	categories,
}: GalleryProps) {
	// Create the image list starting with cover image, then mediaList
	const images: ImageGalleryImage[] = React.useMemo(() => {
		return prepareGalleryImages({
			coverImage,
			mediaList,
			projectId,
			type,
			shouldPrioritizeCoverImage,
		});
	}, [coverImage, mediaList, projectId, type, shouldPrioritizeCoverImage]);

	// Sort categories by unitSpaces priority and filter to only those with images
	const sortedCategories = React.useMemo(() => {
		return getSortedCategories(categories, images);
	}, [categories, images]);

	// Track selected category - select the first category from sorted list
	const [selectedCategory, setSelectedCategory] = React.useState<ImageGalleryCategory | undefined>(sortedCategories[0]);

	if (isLoading) {
		return (
			<div className="w-full h-full bg-gray-100 animate-pulse">
				<div className="w-full h-full bg-gray-200"></div>
			</div>
		);
	}

	return (
		<ImageGallery
			images={images}
			className={`w-full h-full ${className}`}
			showNavigationButtons={images.length > 1}
			showImageCounter={false} // We'll use custom counter
			enableKeyboardNavigation={true}
			enableDoubleTapFullscreen={true}
			autoLoop={true}
			videoHideControls={true}
			// for modern devices (retina/mobile), it will automaticaly download image of 2x or 3x size
			sizes={sizes}
			onMainImageClick={onMainImageClick}
		>
			<GalleryContent
				images={images}
				sortedCategories={sortedCategories}
				selectedCategory={selectedCategory}
				setSelectedCategory={setSelectedCategory}
			/>
		</ImageGallery>
	);
});
